/**
 * PageSkeleton — route-aware loading skeletons.
 *
 * One component maps the current route to a skeleton SHAPE that mirrors that
 * page's real layout, so every page gets its own skeleton automatically instead
 * of one shared generic block. Pages never hand-place skeletons — the route
 * decides the shape here. Built on the already-installed react-loading-skeleton
 * (bones auto-size; theme follows our CSS vars).
 */
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useLocation } from 'react-router-dom';

type Shape = 'list' | 'detail' | 'grid' | 'chat' | 'settings' | 'generic';

// Map a pathname to a layout shape. First match wins; default = generic.
function shapeForPath(path: string): Shape {
  if (path === '/' || path.startsWith('/builder')) return 'chat';
  if (path.startsWith('/hosting/') || path.startsWith('/functions/') || path.startsWith('/pages/')) return 'detail';
  if (path.startsWith('/hosting') || path.startsWith('/functions') || path.startsWith('/pages') || path.startsWith('/email')) return 'list';
  if (path.startsWith('/marketplace') || path.startsWith('/discover') || path.startsWith('/database')) return 'grid';
  if (path.startsWith('/settings')) return 'settings';
  if (path.startsWith('/analytics')) return 'detail';
  return 'generic';
}

function Header() {
  return (
    <div className="space-y-2">
      <Skeleton height={30} width={220} />
      <Skeleton height={14} width={320} />
    </div>
  );
}

function StatRow({ n = 4 }: { n?: number }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Array.from({ length: n }).map((_, i) => <Skeleton key={i} height={80} borderRadius={12} />)}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <SkeletonTheme baseColor="hsl(var(--muted))" highlightColor="hsl(var(--accent) / 0.15)">
      <div className="flex-1 w-full p-6 space-y-5 max-w-6xl mx-auto">{children}</div>
    </SkeletonTheme>
  );
}

export function PageSkeleton() {
  const { pathname } = useLocation();
  const shape = shapeForPath(pathname);

  if (shape === 'chat') {
    // Builder home: centered prompt + suggestion tiles
    return (
      <SkeletonTheme baseColor="hsl(var(--muted))" highlightColor="hsl(var(--accent) / 0.15)">
        <div className="flex flex-col items-center justify-center min-h-[70vh] w-full max-w-2xl mx-auto p-6 space-y-5">
          <Skeleton height={36} width={360} />
          <Skeleton height={110} width="100%" borderRadius={16} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 w-full">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={64} borderRadius={12} />)}
          </div>
        </div>
      </SkeletonTheme>
    );
  }

  if (shape === 'grid') {
    return (
      <Shell>
        <Header />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} height={150} borderRadius={12} />)}
        </div>
      </Shell>
    );
  }

  if (shape === 'detail') {
    return (
      <Shell>
        <Header />
        <StatRow />
        <Skeleton height={34} width={340} />{/* tab bar */}
        <Skeleton height={300} borderRadius={12} />
      </Shell>
    );
  }

  if (shape === 'list') {
    return (
      <Shell>
        <div className="flex items-center justify-between">
          <Header />
          <Skeleton height={36} width={140} borderRadius={10} />
        </div>
        <StatRow />
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={52} borderRadius={10} />)}
        </div>
      </Shell>
    );
  }

  if (shape === 'settings') {
    return (
      <Shell>
        <Header />
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
          <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={32} borderRadius={8} />)}</div>
          <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={90} borderRadius={12} />)}</div>
        </div>
      </Shell>
    );
  }

  // generic
  return (
    <Shell>
      <Header />
      <StatRow />
      <Skeleton height={260} borderRadius={12} />
    </Shell>
  );
}

export default PageSkeleton;
